import { entityService } from '@/services/EntityService';
import { httpClient } from '@/utils/httpClient';
import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import {
  buildInventoryMovementReportHtml,
  type InventoryMovementRow,
  type InventoryMovementTemplateOptions,
} from '@/reports/templates/inventoryMovementTemplate';

interface TransactionLogAsset {
  assetId: string;
  lin: string;
  nsn: string;
  nomenclature: string;
  transQty: number;
  actualPrice?: number | null;
}

interface TransactionLogDetail {
  id: string;
  transactionType: string;
  assets: TransactionLogAsset[];
}

interface TransactionLogResponse {
  transactions: TransactionLogDetail[];
}

interface NsnAggregate {
  lin: string | null;
  nomenclature: string;
  reqQty: number;
  reqCost: number | null;
  disposalQty: number;
  disposalCost: number | null;
}

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const coerceString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeDateOnly = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const parseCatalogUnitCost = (
  entityData: Record<string, unknown> | null | undefined,
): number | null => {
  return parseNumber(entityData?.unit_price);
};

const normalizeLin = (value: unknown): string | null => coerceString(value === '—' ? null : value);

const normalizeNsn = (value: unknown): string => coerceString(value === '—' ? null : value) ?? '—';

const normalizeNomenclature = (value: unknown): string =>
  coerceString(value === '—' ? null : value) ?? '—';

const addCost = (existing: number | null, addition: number | null): number | null => {
  if (addition === null) return existing;
  return (existing ?? 0) + addition;
};

const fetchTransactionLog = async (
  fromDate: string,
  toDate: string,
  types: string,
): Promise<TransactionLogDetail[]> => {
  const query = new URLSearchParams({
    from: fromDate,
    to: toDate,
    types,
    limit: '1000',
  });
  const response = await httpClient.get<TransactionLogResponse>(
    `/api/rest/app/v1/transactions/log?${query.toString()}&source=report`,
  );
  return response.transactions ?? [];
};

export const runInventoryMovementReport = async (
  params: Record<string, unknown>,
): Promise<void> => {
  const fromDate = normalizeDateOnly(params.fromDate);
  const toDate = normalizeDateOnly(params.toDate);

  const [requisitionTransactions, disposalTransactions] = await Promise.all([
    fetchTransactionLog(fromDate, toDate, 'PURCHASE_REQUISITION_CREATE'),
    fetchTransactionLog(fromDate, toDate, 'DISPOSAL'),
  ]);

  const disposalAssetIds = Array.from(
    new Set(
      disposalTransactions
        .flatMap((transaction) => transaction.assets ?? [])
        .map((asset) => coerceString(asset.assetId))
        .filter((assetId): assetId is string => Boolean(assetId)),
    ),
  );

  const catalogIdByAssetId = new Map<string, string | null>();
  const catalogIds = new Set<string>();

  for (const assetIdBatch of chunk(disposalAssetIds, 75)) {
    const idFilter =
      assetIdBatch.length === 1 ? `eq.${assetIdBatch[0]}` : `in.(${assetIdBatch.join(',')})`;
    const assets = await entityService.list({
      type: 'asset',
      limit: assetIdBatch.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const asset of assets) {
      const entityData = (asset.entity_data ?? {}) as Record<string, unknown>;
      const catalogId = coerceString(entityData.catalog_id);
      catalogIdByAssetId.set(asset.id, catalogId);
      if (catalogId) {
        catalogIds.add(catalogId);
      }
    }
  }

  const catalogUnitPriceByCatalogId = new Map<string, number | null>();
  for (const catalogIdBatch of chunk(Array.from(catalogIds), 75)) {
    const idFilter =
      catalogIdBatch.length === 1 ? `eq.${catalogIdBatch[0]}` : `in.(${catalogIdBatch.join(',')})`;
    const catalogs = await entityService.list({
      type: 'catalog',
      limit: catalogIdBatch.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const catalog of catalogs) {
      catalogUnitPriceByCatalogId.set(
        catalog.id,
        parseCatalogUnitCost((catalog.entity_data ?? {}) as Record<string, unknown>),
      );
    }
  }

  const aggregatesByNsn = new Map<string, NsnAggregate>();

  requisitionTransactions.forEach((transaction) => {
    (transaction.assets ?? []).forEach((asset) => {
      const nsn = normalizeNsn(asset.nsn);
      const qty =
        typeof asset.transQty === 'number' && Number.isFinite(asset.transQty) ? asset.transQty : 0;
      const unitPrice = parseNumber(asset.actualPrice);
      const lineCost = unitPrice !== null ? unitPrice * qty : null;

      let entry = aggregatesByNsn.get(nsn);
      if (!entry) {
        entry = {
          lin: normalizeLin(asset.lin),
          nomenclature: normalizeNomenclature(asset.nomenclature),
          reqQty: 0,
          reqCost: null,
          disposalQty: 0,
          disposalCost: null,
        };
        aggregatesByNsn.set(nsn, entry);
      }

      entry.reqQty += qty;
      entry.reqCost = addCost(entry.reqCost, lineCost);
    });
  });

  disposalTransactions.forEach((transaction) => {
    (transaction.assets ?? []).forEach((asset) => {
      const nsn = normalizeNsn(asset.nsn);
      const qty =
        typeof asset.transQty === 'number' && Number.isFinite(asset.transQty) ? asset.transQty : 0;

      const assetId = coerceString(asset.assetId);
      const catalogId = assetId ? (catalogIdByAssetId.get(assetId) ?? null) : null;
      const unitPrice =
        parseNumber(asset.actualPrice) ??
        (catalogId ? (catalogUnitPriceByCatalogId.get(catalogId) ?? null) : null);
      const lineCost = unitPrice !== null ? unitPrice * qty : null;

      let entry = aggregatesByNsn.get(nsn);
      if (!entry) {
        entry = {
          lin: normalizeLin(asset.lin),
          nomenclature: normalizeNomenclature(asset.nomenclature),
          reqQty: 0,
          reqCost: null,
          disposalQty: 0,
          disposalCost: null,
        };
        aggregatesByNsn.set(nsn, entry);
      }

      entry.disposalQty += qty;
      entry.disposalCost = addCost(entry.disposalCost, lineCost);
    });
  });

  const rows: InventoryMovementRow[] = Array.from(aggregatesByNsn.entries())
    .map(([nsn, agg]) => ({
      lin: agg.lin,
      nsn,
      nomenclature: agg.nomenclature,
      reqQty: agg.reqQty,
      reqCost: agg.reqCost,
      disposalQty: agg.disposalQty,
      disposalCost: agg.disposalCost,
      qtyDifference: agg.reqQty - agg.disposalQty,
      costDifference:
        agg.reqCost !== null || agg.disposalCost !== null
          ? (agg.reqCost ?? 0) - (agg.disposalCost ?? 0)
          : null,
    }))
    .sort((a, b) => a.nsn.localeCompare(b.nsn));

  const options: InventoryMovementTemplateOptions = {
    generatedAt: new Date(),
    header: {
      fromDate: fromDate || null,
      toDate: toDate || null,
    },
    rows,
  };

  const html = buildInventoryMovementReportHtml(options);
  outputReport(
    html,
    'inventory-movement-report.html',
    getOutputFormat(params),
    getPreOpenedWindow(params),
  );
};
